import React, { Suspense, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { useLocation } from 'react-router-dom';
import { LoadingState } from '@/components/LoadingState';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { usePageTopBarOptional } from '@/components/layout/PageTopBarContext';
import { getDocumentTitle } from '@/navigation/nav-items';
import { usePageScroll } from '@/hooks/use-page-scroll';
import { rememberPageUrl } from '@/navigation/list-return';
import { pageKey } from '@/navigation/page-key';
import { usePrefScope } from '@/hooks/use-pref-scope';
import { currentEnvironment, environmentLabel } from '@/lib/app-environment';

/** Pages that fill the window below the top bar and scroll inside their own panes (no page scroll). */
const FULL_HEIGHT_PAGES: string[] = ['/chat'];

function under(pages: string[], pathname: string): boolean {
  return pages.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

interface PageLayoutProps {
  children: React.ReactNode;
  className?: string;
  /** Notices above every page (session); the wrapper hides while all render nothing. */
  banner?: React.ReactNode;
}

/**
 * Content area for app pages: fills the main panel with horizontal padding and bottom spacing.
 */
export function PageLayout({ children, className, banner }: PageLayoutProps) {
  const { pathname, search } = useLocation();
  const scopeId = usePrefScope();
  const fullHeight = under(FULL_HEIGHT_PAGES, pathname);
  const recordTitle = usePageTopBarOptional()?.recordTitle ?? null;
  // A new page opens at the top; Back puts you where you were once the data is there.
  usePageScroll(pageKey(pathname), !fullHeight);
  useEffect(() => {
    const label = environmentLabel(currentEnvironment());
    const title = getDocumentTitle(pathname, recordTitle);
    document.title = label ? `[${label}] ${title}` : title;
  }, [pathname, recordTitle]);
  // So breadcrumbs and Back links return to a list as it was left (filters, page).
  useEffect(() => {
    rememberPageUrl(pathname, search, scopeId);
  }, [pathname, search, scopeId]);
  return (
    <div
      className={cn(
        'w-full min-w-0 px-4 pt-4 sm:px-6 md:px-8 md:pt-6',
        // Full-height pages start right under the top bar; their panes bring their own spacing.
        fullHeight ? 'flex h-[calc(100dvh-var(--app-topbar-height))] flex-col pt-0 md:pt-0' : 'min-h-[calc(100dvh-3rem)] pb-16',
        className
      )}
    >
      {/* empty:hidden drops the spacing while the banner renders nothing. Full-height pages are
          flex columns, so the page shrinks to fit under it. */}
      {banner ? (
        <div className={cn('flex shrink-0 flex-col gap-2 empty:hidden', fullHeight ? 'mt-3' : 'mb-4')}>{banner}</div>
      ) : null}
      {/* Pages are lazy-loaded; the sidebar and top bar stay put while one arrives or if it fails.
          A self-routed page keeps its boundary between items, but a crash clears on the next path. */}
      <ErrorBoundary variant="page" key={pageKey(pathname)} resetKeys={[pathname]}>
        <Suspense fallback={<LoadingState label="Loading…" className="py-16" />}>{children}</Suspense>
      </ErrorBoundary>
    </div>
  );
}
